import { Document, Charset } from 'flexsearch'
import { Module } from 'vuex'
import axios from '@/plugins/axios'

const paginate = (page, perPage, collection) => {
    if (collection?.length <= perPage) {
        return collection
    }

    const start = page * perPage - perPage
    const end = Math.min(collection?.length ?? 0, perPage * page)
    return collection?.slice(start, end)
}

const storeModule: Module<any, any> = {
    recordsMaxedOut: false,
    pendingInits: {},
    indexMap: {},
    actions: {
        async initIndex(_, params) {
            // Need to further performance-test this number.

            const MAX_RECORDS = 2000

            if (
                params?.refresh !== true &&
                Object.hasOwn(storeModule.indexMap, params.id)
            ) {
                return
            }

            // Callers that arrive while the list is downloading share that one request (the
            // in-flight promise is kept, not its result).
            if (!storeModule.pendingInits[params.source]) {
                storeModule.pendingInits[params.source] = axios
                    .get(params.source + '?page=1&itemPerPage=' + MAX_RECORDS)
                    .then(({ data }) => (data ? data['hydra:member'] : null))
                    .finally(() => {
                        storeModule.pendingInits[params.source] = null
                    })
            }

            const collection = await storeModule.pendingInits[params.source]

            if (
                params?.refresh !== true &&
                Object.hasOwn(storeModule.indexMap, params.id)
            ) {
                // Another caller built the index while this one waited
                return
            }

            const index = new Document({
                encoder: Charset.Normalize,
				tokenize: 'forward',
				document: {
					id: params.idField,
                	store: params.store ?? true,
					index: params.indexedFields,
				}
            })

            if (collection) {
                if (collection.length == MAX_RECORDS) {
                    // If we have retrieved the maximum number of records, we should assume there's even more.
                    // In the future we should turn off local storage.
                    // storeModule.recordsMaxedOut = true
                    throw 'Too many records exist to store locally'
                }

                for (const record of collection) {
                    index.add(record)
                }
            }

            // Don't commit the index to the state or it will get stored in localStorage, which is unnecessary and can cause us to exceed the localStorage quota

            storeModule.indexMap[params.id] = {
                document: index,
                lastUpdate: Date.now(),
            }
        },
        async indexExists({ commit, state }, index) {
            return Object.hasOwn(storeModule.indexMap, index)
        },

        async addMemberRecord({ commit, state }, member) {
            const indexExists = await this.dispatch('indexExists', 'member')
            if (!indexExists) {
                await this.dispatch('initMemberIndex')
            }
            await this.dispatch('addRecord', {index: 'member', record: member})
        },

        async addRecord({ commit, state }, params) {
            const index = storeModule.indexMap[params.index]
            if (params.record === null) {
            	return
            }
            index.lastUpdate = Date.now()
            return index.document.add(params.record)
        },

        async updateRecord({ commit, state }, params) {
            const index = storeModule.indexMap[params.index]
            index.document.update(params.record)
            index.lastUpdate = Date.now()
        },

        async removeRecord({ commit, state }, params) {
            const index = storeModule.indexMap[params.index]
            index.document.remove(params.id)
            index.lastUpdate = Date.now()
        },

        async setCollection({ commit, state }, params) {
            const index = storeModule.indexMap[params.id]

            for (const record of params.collection) {
                index.document.add(record)
            }

            index.lastUpdate = Date.now()
        },

        async recordExists({ commit, state }, params) {
            const index = storeModule.indexMap[params.index]
            return index.document.contain(params.id)
        },

        async getRecords({ commit, state }, params) {
            const index = storeModule.indexMap[params.index]
            let count = 0
            let items:any = []
            let data = null

            if (params.processes) {
                let storedItems = Object.values(index.document.store)
                for (const process of params.processes) {
                    if (process === undefined) {
                        continue
                    }
                    storedItems = process(storedItems)
                }
            }
            
            if (params.term) {

                items = index?.document?.search(params.term, {
                    index: params.fields,
                    enrich: true,
                    merge: true,
                    // suggest: true,
                }).map(i => i.doc)
                
            } else {
                items = [ ... index?.document?.store.values() ]
            }

            if (params.filters) {
                for (const filter of params.filters) {
                    if (filter === undefined) {
                        continue
                    }
                    items = filter(items)
                }
            }
            

            count = items?.length ?? 0

            if (params.sort) {
                items = params.sort(items)
            }

            if (params.page && params.perPage) {
                items = paginate(params.page, params.perPage, items)
            }

            data = {
                items,
                count,
            }

            return data
        },

        async getRecord({ commit, state }, params) {
            const index = storeModule.indexMap[params.index]
            return index.document.get(params.id)
        },
    },

    mutations: {},
}

export default storeModule
